import React from 'react'
import AntdSpin from 'antd/es/spin'

import { useDesignMode } from 'src/design-mode/useDesignMode'

import type { SpinProps } from './props'
import { Spin as ShadcnSeamSpin, setSeamDefaultIndicator } from './Spin.shadcn'

/**
 * Compat seam leaf for antd's `antd/es/spin` — and the FIFTH component put
 * behind the design toggle, after `../Button`, `../Space`, `../Typography` and
 * `../Tag`.
 *
 * A LEAF, not the `src/antd` barrel, on purpose (PRO-130): the barrel's
 * `export * from 'antd'` pulls antd/es/index.js — the whole component barrel —
 * into whatever chunk imports it, and this module is reachable from the boot
 * path. The barrel re-exports the dispatcher so the 124 files that already
 * write `import { Spin } from 'src/antd'` need no edit.
 *
 * ---------------------------------------------------------------------------
 * THE SWITCH, and its safety property
 *
 * `useDesignMode()` returns `'antd'` for everyone who has not picked "New
 * design" under Theme — which is the shipped default, so it is every page load
 * of every customer who has not opted in. In the `'antd'` branch this component renders antd's own
 * Spin with the props it was given: same element, same class names, same DOM,
 * and — because antd's `delay` default of `0` is left alone there — the same
 * timing. The only difference from `export { default as Spin } from
 * 'antd/es/spin'` — which is what the barrel gave call sites before — is one
 * extra component in the React tree, which renders nothing of its own.
 * `../__tests__/spin-seam-parity.test.tsx` asserts that byte for byte across a
 * prop matrix, and it is the test to keep green: `development` continuing to
 * ship antd unchanged to customers is the whole premise of shipping shadcn one
 * component at a time.
 *
 * WHY SPIN. It is the app's loading language: 135 tags across 124 files, plus
 * the route-chunk fallback every navigation goes through. The captain's
 * complaint that started this port was about how loading FEELS, so the surface
 * to port is the one the reader waits on. It also carries no popup and no
 * portal — nothing to measure, nothing to clip.
 *
 * antd's one static is carried across explicitly, NOT by copying antd's
 * component object. `setDefaultIndicator` installs an app-wide default
 * spinner by mutating a module-level variable inside antd; the shadcn branch
 * keeps its own, so this writes BOTH and the two modes cannot end up
 * disagreeing about what the app's default spinner is. Nothing in this repo
 * calls THIS one today — `./AntdDefaultIndicator.tsx` deliberately writes
 * antd's half alone, because it exists to reach the Spins antd renders inside
 * its own Table and List and must leave the seam's branch on its own
 * indicator. That file says why.
 */
const SpinDispatcher = (props: SpinProps) => {
  const designMode = useDesignMode()

  if (designMode === 'shadcn') return <ShadcnSeamSpin {...props} />

  return <AntdSpin {...props} />
}

SpinDispatcher.displayName = 'Spin'

export const Spin = Object.assign(SpinDispatcher, {
  setDefaultIndicator: (indicator: React.ReactNode) => {
    AntdSpin.setDefaultIndicator(indicator)
    setSeamDefaultIndicator(indicator)
  },
})

export default Spin
